import { useRef, useState } from 'react';
import styles from './style.module.css';
import { ILandingBusiness } from '../../interfaces/ILandingBusiness';
import { getStateAbbreviation } from '../../utils/brazilianStates';
import {
  APARTMENT_TYPES,
  formatSquareMeters,
} from '../../utils/apartmentTypes';
// Selos oficiais da Prefeitura de SP, usados como imagem original (os mesmos
// da LP de incorporadora). A Prefeitura não permite editar o selo.
import habitacaoMercadoPopular from '../../assets/images/habitacaoMercadoPopular.png';
import habitacaoMercadoPopularInteresseSocial from '../../assets/images/habitacaoMercadoPopularInteresseSocial.png';

function CoverImage({ src }: { src: string }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [imgStyle, setImgStyle] = useState<React.CSSProperties>({
    visibility: 'hidden',
  });

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const cw = wrap.offsetWidth;
    const ch = wrap.offsetHeight;
    const iw = e.currentTarget.naturalWidth;
    const ih = e.currentTarget.naturalHeight;
    if (!iw || !ih || !cw || !ch) return;
    const scale = Math.max(cw / iw, ch / ih);
    const dw = Math.round(iw * scale);
    const dh = Math.round(ih * scale);
    setImgStyle({
      position: 'absolute',
      width: dw,
      height: dh,
      left: Math.round((cw - dw) / 2),
      top: Math.round((ch - dh) / 2),
    });
  };

  return (
    <div
      ref={wrapRef}
      style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}
    >
      <img src={src} alt="" style={imgStyle} onLoad={handleLoad} />
    </div>
  );
}

interface IPosterPrintLandingBusiness {
  landingBusiness: ILandingBusiness;
  playerName?: string;
  playerPhone?: string;
  playerPhoto?: string;
  playerRating?: number;
  clientLogo?: string;
  color: string;
  imagesBase64?: string[];
  qrCode?: string;
  showPlayerContact?: boolean;
}

function getConstructionStageLabel(
  stage: ILandingBusiness['details']['constructionStage']
): string | null {
  if (!stage) return null;
  if (stage.upcomingLaunch) return 'Pré-lançamento';
  if (stage.launched) return 'Lançamento';
  if (stage.underConstruction) return 'Em obras';
  if (stage.advancedConstruction) return 'Obras avançadas';
  if (stage.readyToMoveIn) return 'Pronto para morar';
  if (stage.blueprint) return 'Na planta';
  return null;
}

function formatRange(val?: number | number[]): string {
  if (val === undefined || val === null) return '';
  if (Array.isArray(val)) {
    const unique = [...new Set(val)];
    if (unique.length === 1) return `${unique[0]}`;
    return `${unique[0]} – ${unique[unique.length - 1]}`;
  }
  return `${val}`;
}

export function PosterPrintLandingBusiness({
  landingBusiness,
  playerName,
  playerPhone,
  playerPhoto,
  playerRating,
  clientLogo,
  color,
  imagesBase64,
  qrCode,
  showPlayerContact = true,
}: IPosterPrintLandingBusiness) {
  const { details, medias } = landingBusiness;

  // Build image pool: prioritize housingUnit images, fill from commonAreas, max 4
  const rawImages = [
    ...(medias?.images?.housingUnitImages || []),
    ...(medias?.images?.commonAreasImages || []),
  ].slice(0, 4);

  const displayImages =
    imagesBase64 && imagesBase64.length > 0
      ? imagesBase64.slice(0, 4)
      : rawImages;

  const heroImage = displayImages[0];
  const mosaicImages = displayImages.slice(1, 4);

  const stageLabel = getConstructionStageLabel(details?.constructionStage);
  const sqm = formatRange(details?.squareMeters);
  const beds = formatRange(details?.bedrooms);
  const baths = details?.bathrooms;
  const apartmentTypes = APARTMENT_TYPES.filter(
    ({ key }) => details?.apartmentType?.[key]
  )
    .map(({ key, label }) => ({
      key,
      label,
      squareMeters: formatSquareMeters(
        details?.apartmentTypeSquareMeters?.[key]
      ),
    }))
    .filter(({ squareMeters }) => squareMeters);
  const deliveryDate = details?.deliveryDate;

  const stateCity = [
    details?.location?.city,
    getStateAbbreviation(details?.location?.state),
  ]
    .filter(Boolean)
    .join(' – ');
  const neighborhoodLabel =
    details?.location?.neighborhood || details?.location?.region || '';

  const address = [
    details?.location?.neighborhood,
    details?.location?.city,
    details?.location?.state,
  ]
    .filter(Boolean)
    .join(', ');

  const footerText = details?.posterFooter || address;

  const roundedRating = Math.round(playerRating || 0);

  const showPodeEntrar = details?.paymentConditions?.podeEntrar;
  const showCasaVerdeAmarela = details?.paymentConditions?.casaVerdeAmarela;
  const paymentConditionSeal = showCasaVerdeAmarela
    ? {
        src: habitacaoMercadoPopularInteresseSocial,
        alt: 'Habitação de Mercado Popular e Habitação de Interesse Social',
      }
    : showPodeEntrar
      ? { src: habitacaoMercadoPopular, alt: 'Habitação de Mercado Popular' }
      : null;
  const hasPaymentConditionBadges = !!paymentConditionSeal;

  return (
    <div className={styles.print}>
      <div className={styles.poster} id="print">
        {/* Top bar */}
        <div className={styles.topBar} style={{ background: color }}>
          <span>{stateCity}</span>
          <span className={styles.topBarPin}>
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              style={{ fill: '#fff' }}
            >
              <path
                style={{ fill: '#fff' }}
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"
              />
            </svg>
          </span>
          <span>{neighborhoodLabel}</span>
        </div>

        {/* Title */}
        <div className={styles.titleSection} style={{ background: color }}>
          <div className={styles.title}>{details?.name}</div>
        </div>

        {/* Hero image */}
        {heroImage && (
          <div className={styles.heroContainer}>
            <div className={styles.heroWrapper}>
              <CoverImage src={heroImage} />
            </div>
            {stageLabel && (
              <div className={styles.stageBadge} style={{ background: color }}>
                {stageLabel}
              </div>
            )}
          </div>
        )}

        {/* Tagline / summary */}
        {details?.summary && (
          <div className={styles.taglineSection}>
            <div className={styles.tagline}>{details.summary}</div>
          </div>
        )}

        {/* Info row: logo + amenities */}
        <div className={styles.infoRow}>
          {clientLogo && (
            <div className={styles.clientLogoWrapper}>
              <img src={clientLogo} alt="logo" className={styles.clientLogo} />
            </div>
          )}
          <div className={styles.infoItems}>
            {sqm && <span className={styles.infoItem}>{sqm} m²</span>}
            {beds && beds !== '0' && (
              <span className={styles.infoItem}>
                {beds} {beds === '1' ? 'quarto' : 'quartos'}
              </span>
            )}
            {baths !== undefined && baths !== null && (
              <span className={styles.infoItem}>
                {baths} {baths === 1 ? 'banheiro' : 'banheiros'}
              </span>
            )}
            {deliveryDate && (
              <span className={styles.infoItem}>
                Entrega:{' '}
                {(() => {
                  const d = new Date(deliveryDate);
                  const month = d.toLocaleDateString('pt-BR', {
                    month: 'short',
                    timeZone: 'UTC',
                  });
                  const year = d.getUTCFullYear();
                  return `${month.charAt(0).toUpperCase()}${month.slice(1).replace('.', '')}. ${year}`;
                })()}
              </span>
            )}
          </div>
        </div>

        {/* Metragem por tipo de unidade */}
        {apartmentTypes.length > 0 && (
          <div className={styles.apartmentTypesRow}>
            {apartmentTypes.map(({ key, label, squareMeters }) => (
              <span key={key} className={styles.infoItem}>
                {label}, {squareMeters}
              </span>
            ))}
          </div>
        )}

        {/* Image mosaic */}
        {mosaicImages.length > 0 && (
          <div className={styles.mosaic}>
            {mosaicImages.length === 1 && (
              <div className={styles.mosaicFull}>
                <CoverImage src={mosaicImages[0]} />
              </div>
            )}
            {mosaicImages.length === 2 && (
              <>
                <div className={styles.mosaicHalf}>
                  <CoverImage src={mosaicImages[0]} />
                </div>
                <div className={styles.mosaicHalf}>
                  <CoverImage src={mosaicImages[1]} />
                </div>
              </>
            )}
            {mosaicImages.length === 3 && (
              <>
                <div className={styles.mosaicLarge}>
                  <CoverImage src={mosaicImages[0]} />
                </div>
                <div className={styles.mosaicStack}>
                  <div className={styles.mosaicSmall}>
                    <CoverImage src={mosaicImages[1]} />
                  </div>
                  <div className={styles.mosaicSmall}>
                    <CoverImage src={mosaicImages[2]} />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Selos + QR Code + Agent (com telefone abaixo) — same row */}
        <div
          className={styles.bottomRow}
          style={
            !hasPaymentConditionBadges && !showPlayerContact
              ? { justifyContent: 'center' }
              : undefined
          }
        >
          {/* Left: selo oficial da Prefeitura de SP, usado como imagem
              original (a Prefeitura não permite editar o selo). Só um selo
              por cartaz: "HMP e HIS" quando essa condição está ativa (ele já
              cobre HMP), senão "HMP". */}
          {paymentConditionSeal && (
            <div className={styles.paymentConditionBadges}>
              <img
                src={paymentConditionSeal.src}
                alt={paymentConditionSeal.alt}
                className={styles.paymentConditionBadge}
              />
            </div>
          )}

          {/* Center: QR Code */}
          {qrCode && (
            <div className={styles.qrSection}>
              <div className={styles.qrLabel}>
                Leia o QR Code para mais detalhes:
              </div>
              <img src={qrCode} alt="QR Code" className={styles.qrImage} />
            </div>
          )}

          {/* Right: Agent (foto, nota, nome e, abaixo, telefone) */}
          {showPlayerContact && (
            <div className={styles.contactRight}>
              {playerPhoto && (
                <img
                  src={playerPhoto}
                  alt={playerName}
                  className={styles.agentPhoto}
                  style={{ borderColor: color }}
                />
              )}
              <div className={styles.agentDetails}>
                {playerRating !== undefined && playerRating > 0 && (
                  <div className={styles.starsRow}>
                    <span style={{ color: '#ffc107' }}>
                      {'★'.repeat(roundedRating)}
                      {'☆'.repeat(5 - roundedRating)}
                    </span>
                    <span className={styles.ratingNum}>
                      {playerRating.toFixed(2)}
                    </span>
                  </div>
                )}
                {playerName && (
                  <div className={styles.agentName}>{playerName}</div>
                )}
              </div>
              {playerPhone && (
                <div className={styles.contactBelow}>
                  <div className={styles.phoneRow}>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="#25D366"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    <span className={styles.phoneText}>{playerPhone}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom bar */}
        <div className={styles.bottomBar} style={{ background: color }}>
          <span>{footerText}</span>
        </div>
      </div>
    </div>
  );
}
